import React, { createContext, useContext, useState } from 'react'
import * as FloatingUi from '@floating-ui/react'
import xIcon from '../../../../assets/images/close-x.svg'

const ModalContext = createContext({ isOpen: false, setIsOpen: function (bool: boolean) {} })

interface CloseButtonProps extends React.HTMLAttributes<HTMLButtonElement> {}

export const CloseButton: React.FC<CloseButtonProps> = (props) => {
  const { setIsOpen } = useContext(ModalContext)

  return (
    <button
      {...props}
      onClick={(e) => {
        setIsOpen(false)
        props?.onClick?.(e)
      }}
    />
  )
}

interface ModalOptions {
  anchor?: React.ReactNode
  children?: React.ReactNode
}

// https://floating-ui.com/docs/dialog
const Modal: React.FC<ModalOptions> = ({
  anchor,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, context } = FloatingUi.useFloating({
    open: isOpen,
    onOpenChange: setIsOpen
  })

  const click = FloatingUi.useClick(context)
  const role = FloatingUi.useRole(context)
  const dismiss = FloatingUi.useDismiss(context, { outsidePressEvent: 'mousedown' })

  const { getReferenceProps, getFloatingProps } = FloatingUi.useInteractions([
    click,
    role,
    dismiss
  ])

  const closeButton = (
    <CloseButton className='floating-ui-modal-close-button' aria-label='Close'>
      <img src={xIcon} className='floating-ui-modal-close-icon' />
    </CloseButton>
  )

  return (
    <>
      <ModalContext.Provider value={{ isOpen, setIsOpen }}>
        <div ref={refs.setReference} {...getReferenceProps()}>
          {anchor}
        </div>
        <FloatingUi.FloatingPortal>
          {isOpen && (
            <FloatingUi.FloatingOverlay className='floating-ui-modal-overlay' lockScroll>
              <FloatingUi.FloatingFocusManager context={context}>
                <div
                  className='floating-ui-modal'
                  ref={refs.setFloating}
                  {...getFloatingProps()}
                >
                  {closeButton}
                  {children}
                </div>
              </FloatingUi.FloatingFocusManager>
            </FloatingUi.FloatingOverlay>
          )}
        </FloatingUi.FloatingPortal>
      </ModalContext.Provider>
    </>
  )
}

export default Modal
